import { useEffect, useRef, useState } from "react";
import { mediaUrl } from "@/lib/api";

export const PRINT_SHAPES = [
  { id: "rect", label: "Rectangle", testId: "pf-print-shape-rect" },
  { id: "square", label: "Square", testId: "pf-print-shape-square" },
  { id: "circle", label: "Circle", testId: "pf-print-shape-circle" },
  { id: "triangle", label: "Triangle", testId: "pf-print-shape-triangle" },
  { id: "polygon", label: "Trace shape", testId: "pf-print-shape-polygon" },
];

export const MAX_POLYGON_POINTS = 12;

export const PRINT_FONTS = [
  { id: "sans", label: "Sans", family: "Inter, system-ui, sans-serif", google: "Inter" },
  { id: "serif", label: "Serif", family: "Georgia, 'Times New Roman', serif" },
  { id: "mono", label: "Typewriter", family: "'Courier New', Courier, monospace" },
  { id: "script", label: "Script", family: "'Dancing Script', cursive", google: "Dancing Script" },
  { id: "marker", label: "Marker", family: "'Permanent Marker', cursive", google: "Permanent Marker" },
  { id: "display", label: "Bold", family: "Oswald, Impact, sans-serif", google: "Oswald" },
];

export function printFontFamily(id) {
  return (PRINT_FONTS.find((f) => f.id === id) || PRINT_FONTS[0]).family;
}

export function ensurePrintFontsLoaded() {
  if (typeof document === "undefined") return;
  const id = "cp-google-fonts";
  if (document.getElementById(id)) return;
  const families = PRINT_FONTS.map((f) => f.google).filter(Boolean);
  const params = families.map((f) => `family=${encodeURIComponent(f).replace(/%20/g, "+")}:wght@400;700`).join("&");
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?${params}&display=swap`;
  document.head.appendChild(link);
}

export function polygonClosed(area) {
  const pts = area?.points || [];
  if (pts.length < 3) return false;
  return area?.closed !== false;
}

export function emptyPrintConfig() {
  return {
    enabled: false,
    base_image_url: "",
    allow: "both",
    print_area: { shape: "rect", x: 18, y: 18, w: 64, h: 64 },
  };
}

export function clipPathFor(area) {
  const shape = typeof area === "string" ? area : area?.shape;
  if (shape === "circle") return "circle(50% at 50% 50%)";
  if (shape === "triangle") return "polygon(50% 0%, 0% 100%, 100% 100%)";
  if (shape === "polygon" && polygonClosed(area)) {
    return `polygon(${area.points.map((p) => `${p.x}% ${p.y}%`).join(", ")})`;
  }
  return "none";
}

export function printAreaStyle(area) {
  if (!area) return { display: "none" };
  if (area.shape === "polygon" && polygonClosed(area)) {
    return {
      position: "absolute",
      left: 0,
      top: 0,
      width: "100%",
      height: "100%",
      overflow: "hidden",
      clipPath: clipPathFor(area),
      WebkitClipPath: clipPathFor(area),
    };
  }
  return {
    position: "absolute",
    left: `${area.x}%`,
    top: `${area.y}%`,
    width: `${area.w}%`,
    height: `${area.h}%`,
    overflow: "hidden",
    clipPath: clipPathFor(area),
    WebkitClipPath: clipPathFor(area),
  };
}

export function isPrintOn(p) {
  const c = p?.custom_print;
  if (!c?.enabled || !c?.base_image_url || !c?.print_area) return false;
  if (c.print_area.shape === "polygon") return polygonClosed(c.print_area);
  return true;
}

export function loadImg(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("image"));
    img.src = mediaUrl(src);
  });
}

function clipCanvas(ctx, area, W, H) {
  const x = (area.x / 100) * W;
  const y = (area.y / 100) * H;
  const w = (area.w / 100) * W;
  const h = (area.h / 100) * H;
  ctx.beginPath();
  if (area.shape === "polygon" && polygonClosed(area)) {
    area.points.forEach((p, i) => {
      const px = (p.x / 100) * W;
      const py = (p.y / 100) * H;
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    });
    ctx.closePath();
  } else if (area.shape === "circle") {
    ctx.ellipse(x + w / 2, y + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
  } else if (area.shape === "triangle") {
    ctx.moveTo(x + w / 2, y);
    ctx.lineTo(x, y + h);
    ctx.lineTo(x + w, y + h);
    ctx.closePath();
  } else {
    ctx.rect(x, y, w, h);
  }
  ctx.clip();
  return { x, y, w, h };
}

export async function renderPrintPreviewBlob({ baseUrl, printArea, image, text }) {
  const base = await loadImg(baseUrl);
  const W = base.naturalWidth || 800;
  const H = base.naturalHeight || 800;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(base, 0, 0, W, H);
  ctx.save();
  const box = clipCanvas(ctx, printArea, W, H);
  if (image?.url) {
    try {
      const art = await loadImg(image.url);
      const scale = Number(image.scale || 1);
      const cx = box.x + (Number(image.x ?? 50) / 100) * box.w;
      const cy = box.y + (Number(image.y ?? 50) / 100) * box.h;
      const iw = box.w * scale;
      const ih = (art.naturalHeight / art.naturalWidth) * iw;
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate((Number(image.rotation || 0) * Math.PI) / 180);
      ctx.drawImage(art, -iw / 2, -ih / 2, iw, ih);
      ctx.restore();
    } catch {
      /* skip art if CORS blocks it */
    }
  }
  if (text?.value) {
    const cx = box.x + (Number(text.x ?? 50) / 100) * box.w;
    const cy = box.y + (Number(text.y ?? 50) / 100) * box.h;
    const size = Math.max(8, (Number(text.size || 12) / 100) * box.h);
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate((Number(text.rotation || 0) * Math.PI) / 180);
    ctx.fillStyle = text.color || "#111111";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = `700 ${size}px ${printFontFamily(text.font)}`;
    ctx.fillText(String(text.value).slice(0, 120), 0, 0);
    ctx.restore();
  }
  ctx.restore();
  return new Promise((resolve) => {
    canvas.toBlob((b) => resolve(b), "image/jpeg", 0.88);
  });
}

export function PrintLayers({ printArea, image, text, interactive, layerRef, onLayerPointerDown }) {
  const innerRef = useRef(null);
  const [boxH, setBoxH] = useState(0);
  useEffect(() => {
    ensurePrintFontsLoaded();
  }, []);
  useEffect(() => {
    const el = innerRef.current;
    if (!el) return undefined;
    const apply = () => setBoxH(el.clientHeight || 0);
    apply();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, [printArea, text?.value, image?.url]);

  if (!printArea) return null;
  const poly = printArea.shape === "polygon" && polygonClosed(printArea);
  const innerStyle = poly
    ? { left: `${printArea.x}%`, top: `${printArea.y}%`, width: `${printArea.w}%`, height: `${printArea.h}%` }
    : { inset: 0 };
  const textPx = Math.max(10, ((Number(text?.size) || 14) / 100) * (boxH || 160));
  return (
    <div style={printAreaStyle(printArea)} className="pointer-events-none">
      <div
        ref={(el) => {
          innerRef.current = el;
          if (typeof layerRef === "function") layerRef(el);
          else if (layerRef) layerRef.current = el;
        }}
        className={`absolute overflow-visible ${interactive ? "pointer-events-auto" : ""}`}
        style={innerStyle}
        onPointerDown={interactive && image?.url ? (e) => {
          if (e.target?.closest?.("[data-cp-layer='text']")) return;
          onLayerPointerDown?.("image", e);
        } : undefined}
      >
        {image?.url ? (
          <img
            src={mediaUrl(image.url)}
            alt=""
            data-cp-layer="image"
            className={`absolute max-w-none ${interactive ? "cursor-move" : ""}`}
            style={{
              left: `${image.x ?? 50}%`,
              top: `${image.y ?? 50}%`,
              width: `${(image.scale || 1) * 100}%`,
              height: "auto",
              transform: `translate(-50%, -50%) rotate(${image.rotation || 0}deg)`,
              pointerEvents: interactive ? "auto" : "none",
            }}
          />
        ) : null}
        {text?.value ? (
          <div
            data-cp-layer="text"
            data-testid="cp-text-layer"
            className={`absolute font-bold whitespace-nowrap select-none px-1 py-0.5 ${interactive ? "cursor-move pointer-events-auto" : "pointer-events-none"}`}
            style={{
              left: `${text.x ?? 50}%`,
              top: `${text.y ?? 50}%`,
              fontSize: `${textPx}px`,
              fontFamily: printFontFamily(text.font),
              color: text.color || "#111111",
              transform: `translate(-50%, -50%) rotate(${text.rotation || 0}deg)`,
              lineHeight: 1,
            }}
            onPointerDown={interactive ? (e) => {
              e.stopPropagation();
              onLayerPointerDown?.("text", e);
            } : undefined}
          >
            {text.value}
          </div>
        ) : null}
      </div>
    </div>
  );
}
